import { domainError, type DomainError } from '../shared/domain-error';
import { normalizeName } from '../shared/naming';
import { err, ok, type Result } from '../shared/result';
import type { StructuralPieceId, UnitTypeId } from '../shared/ids';
import { roundQuantity } from '../schedule/unit-of-measure';
import { pointInPolygon } from './foundation';
import type { ItemFactor } from './own-factor';
import type { SteelRate } from './steel';
import { pieceNameTooLong, pieceNameTooLongMessage } from './pile';
import {
  exactPolygonArea,
  isSimplePolygon,
  isValidOutline,
  polygonArea,
  polygonPerimeter,
  type Point,
} from './polygon';

/**
 * O radier e a laje (Fase 27, etapa 3 — D7, D8, F13, F14, RN-369 a RN-371).
 *
 * Os dois são o mesmo desenho: um contorno com espessura e, por cima, **regiões** onde a
 * espessura é outra — o engrossamento sob o pilar no radier, o rebaixo do banheiro na laje.
 * Dentro da região vale a espessura dela; no resto, a da peça. A área não muda com a região:
 * a fôrma de fundo e o escoramento continuam sendo o contorno inteiro (RN-369).
 *
 * O radier é da Fundação (área comum do desenho dela, RN-361). A laje é do pavimento: fica na
 * área comum do desenho do andar, porque atravessa todas as tipologias dele (F3).
 *
 * > **As regras em uma linha:** a região fica dentro do contorno e não sobrepõe outra
 * > (RN-369); o shaft tira a área e o volume dele da laje (RN-370); onde há laje, a viga conta
 * > só abaixo dela (RN-371).
 */

export type SlabKind = 'RADIER' | 'LAJE';

export const SLAB_KIND_LABEL: Readonly<Record<SlabKind, string>> = {
  RADIER: 'Radier',
  LAJE: 'Laje',
};

/** Uma região de espessura diferente, dentro do contorno da peça. */
export interface SlabRegion {
  readonly outline: readonly Point[];
  /** Metros. Vale dentro da região, no lugar da espessura da peça. */
  readonly thickness: number;
}

export interface Slab {
  readonly id: StructuralPieceId;
  readonly unitTypeId: UnitTypeId;
  readonly kind: SlabKind;
  /** "Radier", "L1" — único entre as peças do mesmo tipo no desenho. */
  readonly name: string;
  /** Metros, nas coordenadas da tipologia, como o contorno do ambiente. */
  readonly outline: readonly Point[];
  /** Metros. 0,25 no radier, 0,10 na laje. */
  readonly thickness: number;
  readonly regions: readonly SlabRegion[];
  /**
   * O fator próprio de cada item neste radier, por cima do índice da obra (Fase 41, RN-538). Só
   * o radier: a laje do pavimento é do apartamento (Fase 40).
   */
  readonly factors?: readonly ItemFactor[];
  /**
   * A taxa de aço própria desta peça, kg/m³ por bitola (Fase 41, etapa 2 — RN-544). Vale inteira,
   * por cima da do tipo e da disciplina. Ausente: o rascunho de antes; vazia: nenhuma.
   */
  readonly steelRates?: readonly SteelRate[];
  readonly notes?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

export const SLAB_ERROR = {
  NAME_REQUIRED: 'TAKEOFF.SLAB_NAME_REQUIRED',
  NAME_TAKEN: 'TAKEOFF.SLAB_NAME_TAKEN',
  NAME_TOO_LONG: 'TAKEOFF.SLAB_NAME_TOO_LONG',
  INVALID_OUTLINE: 'TAKEOFF.SLAB_INVALID_OUTLINE',
  INVALID_THICKNESS: 'TAKEOFF.SLAB_INVALID_THICKNESS',
  REGION_OUTSIDE: 'TAKEOFF.SLAB_REGION_OUTSIDE',
  REGIONS_OVERLAP: 'TAKEOFF.SLAB_REGIONS_OVERLAP',
} as const;

/** Abaixo disto o contorno não tem área: três pontos em linha reta. m². */
const AREA_MINIMA = 0.0001;

function positivo(valor: number): boolean {
  return Number.isFinite(valor) && valor > 0;
}

function tres(valor: number): number {
  return Math.round(valor * 1000) / 1000;
}

function contornoValido(outline: readonly Point[]): boolean {
  return isValidOutline(outline) && polygonArea(outline) >= AREA_MINIMA;
}

// ── Geometria de dois polígonos ─────────────────────────────────────────────────

function cruzam(a: Point, b: Point, c: Point, d: Point): boolean {
  const lado = (p: Point, q: Point, r: Point) =>
    (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);
  const d1 = lado(c, d, a);
  const d2 = lado(c, d, b);
  const d3 = lado(a, b, c);
  const d4 = lado(a, b, d);
  // Só o cruzamento próprio: encostar numa aresta não é sobrepor.
  return d1 * d2 < -1e-12 && d3 * d4 < -1e-12;
}

function arestasCruzam(a: readonly Point[], b: readonly Point[]): boolean {
  for (let i = 0; i < a.length; i++) {
    for (let j = 0; j < b.length; j++) {
      if (cruzam(a[i]!, a[(i + 1) % a.length]!, b[j]!, b[(j + 1) % b.length]!)) return true;
    }
  }
  return false;
}

function centroide(outline: readonly Point[]): Point {
  const n = outline.length;
  return {
    x: outline.reduce((soma, p) => soma + p.x, 0) / n,
    y: outline.reduce((soma, p) => soma + p.y, 0) / n,
  };
}

/**
 * O polígono `inner` está dentro de `outer`? Nenhuma aresta cruza a do contorno e o miolo
 * está dentro. O vértice em cima da borda vale como dentro: a região que encosta no contorno
 * é a borda engrossada, e é comum.
 */
export function polygonInside(inner: readonly Point[], outer: readonly Point[]): boolean {
  if (arestasCruzam(inner, outer)) return false;
  return pointInPolygon(centroide(inner), outer);
}

/** Os dois polígonos se sobrepõem? Encostar lado a lado não conta. */
export function polygonsOverlap(a: readonly Point[], b: readonly Point[]): boolean {
  if (arestasCruzam(a, b)) return true;
  return pointInPolygon(centroide(a), b) || pointInPolygon(centroide(b), a);
}

// ── A conferência ───────────────────────────────────────────────────────────────

export function checkSlabFields(
  kind: SlabKind,
  fields: {
    readonly name: string;
    readonly outline: readonly Point[];
    readonly thickness: number;
    readonly regions: readonly SlabRegion[];
  },
  siblings: readonly Pick<Slab, 'id' | 'kind' | 'name' | 'deletedAt'>[],
  selfId?: StructuralPieceId,
): Result<
  {
    readonly name: string;
    readonly outline: readonly Point[];
    readonly thickness: number;
    readonly regions: readonly SlabRegion[];
  },
  DomainError
> {
  const rotulo = SLAB_KIND_LABEL[kind].toLowerCase();
  const nome = fields.name.trim();
  if (nome === '') {
    return err(domainError(SLAB_ERROR.NAME_REQUIRED, `O nome do ${rotulo} é obrigatório.`));
  }
  if (pieceNameTooLong(nome)) {
    return err(domainError(SLAB_ERROR.NAME_TOO_LONG, pieceNameTooLongMessage(rotulo)));
  }
  const chave = normalizeName(nome);
  if (
    siblings.some(
      (outra) =>
        outra.deletedAt === undefined &&
        outra.kind === kind &&
        outra.id !== selfId &&
        normalizeName(outra.name) === chave,
    )
  ) {
    return err(
      domainError(
        SLAB_ERROR.NAME_TAKEN,
        `Já existe ${kind === 'LAJE' ? 'a laje' : 'o radier'} ${nome} neste desenho.`,
        {
          name: nome,
        },
      ),
    );
  }
  if (isValidOutline(fields.outline) && !isSimplePolygon(fields.outline)) {
    return err(
      domainError(
        SLAB_ERROR.INVALID_OUTLINE,
        `O contorno se cruza: trace ${nome} de novo, sem lados que se atravessem.`,
      ),
    );
  }
  if (!contornoValido(fields.outline)) {
    return err(
      domainError(
        SLAB_ERROR.INVALID_OUTLINE,
        `O contorno de ${nome} precisa de ao menos três pontos e área maior que zero.`,
      ),
    );
  }
  if (!positivo(fields.thickness)) {
    return err(
      domainError(SLAB_ERROR.INVALID_THICKNESS, `A espessura de ${nome} deve ser maior que zero.`, {
        thickness: fields.thickness,
      }),
    );
  }
  for (const [indice, regiao] of fields.regions.entries()) {
    if (isValidOutline(regiao.outline) && !isSimplePolygon(regiao.outline)) {
      return err(
        domainError(
          SLAB_ERROR.INVALID_OUTLINE,
          `O contorno se cruza na região ${String(indice + 1)} de ${nome}: trace-a de novo.`,
        ),
      );
    }
    if (!contornoValido(regiao.outline) || !positivo(regiao.thickness)) {
      return err(
        domainError(
          SLAB_ERROR.INVALID_THICKNESS,
          `A região ${String(indice + 1)} de ${nome} precisa de contorno e espessura maior que zero.`,
        ),
      );
    }
    // A região que sai do contorno é recusada, e não recortada (RN-369): recortar em silêncio
    // mudaria o concreto sem ninguém ver onde.
    if (!polygonInside(regiao.outline, fields.outline)) {
      return err(
        domainError(
          SLAB_ERROR.REGION_OUTSIDE,
          `A região ${String(indice + 1)} sai do contorno de ${nome}. Desenhe-a dentro.`,
        ),
      );
    }
    const anterior = fields.regions
      .slice(0, indice)
      .findIndex((outra) => polygonsOverlap(outra.outline, regiao.outline));
    if (anterior >= 0) {
      return err(
        domainError(
          SLAB_ERROR.REGIONS_OVERLAP,
          `As regiões ${String(anterior + 1)} e ${String(indice + 1)} de ${nome} se sobrepõem.`,
        ),
      );
    }
  }
  return ok({
    name: nome,
    outline: fields.outline,
    thickness: tres(fields.thickness),
    regions: fields.regions.map((regiao) => ({
      outline: regiao.outline,
      thickness: tres(regiao.thickness),
    })),
  });
}

// ── As contas ───────────────────────────────────────────────────────────────────

/**
 * Um buraco na laje — o shaft, com os dois cantos e quantos iguais (RN-370). Vem das
 * tipologias do mesmo desenho: o shaft é do ambiente do apto, e a laje é da área comum.
 */
export interface SlabOpening {
  readonly from: Point;
  readonly to: Point;
  readonly count: number;
}

function retanguloDe(opening: SlabOpening): Point[] {
  const [x0, x1] = [Math.min(opening.from.x, opening.to.x), Math.max(opening.from.x, opening.to.x)];
  const [y0, y1] = [Math.min(opening.from.y, opening.to.y), Math.max(opening.from.y, opening.to.y)];
  return [
    { x: x0, y: y0 },
    { x: x1, y: y0 },
    { x: x1, y: y1 },
    { x: x0, y: y1 },
  ];
}

/** A espessura no ponto: a da região que o contém, ou a da peça. */
export function thicknessAt(slab: Pick<Slab, 'thickness' | 'regions'>, point: Point): number {
  return (
    slab.regions.find((regiao) => pointInPolygon(point, regiao.outline))?.thickness ??
    slab.thickness
  );
}

/** Os buracos que caem na peça: o centro do shaft dentro do contorno. */
function buracosDentro(
  slab: Pick<Slab, 'outline'>,
  openings: readonly SlabOpening[],
): SlabOpening[] {
  return openings.filter((buraco) => pointInPolygon(centroide(retanguloDe(buraco)), slab.outline));
}

/**
 * A área da peça — fôrma de fundo, escoramento, regularização. O contorno inteiro, sem os
 * shafts que caem nele (RN-370); a região não muda a área (RN-369). m², duas casas.
 */
export function slabArea(
  slab: Pick<Slab, 'outline'>,
  openings: readonly SlabOpening[] = [],
): number {
  const buracos = buracosDentro(slab, openings).reduce(
    (soma, buraco) => soma + exactPolygonArea(retanguloDe(buraco)) * buraco.count,
    0,
  );
  return roundQuantity(Math.max(0, exactPolygonArea(slab.outline) - buracos), 2);
}

/**
 * O concreto: a área de cada região na espessura dela, o resto na espessura da peça, menos os
 * shafts na espessura de onde eles caem (RN-369, RN-370). m³, três casas.
 */
export function slabVolume(
  slab: Pick<Slab, 'outline' | 'thickness' | 'regions'>,
  openings: readonly SlabOpening[] = [],
): number {
  // Áreas exatas: o volume só arredonda no fim (a de duas casas é a que se mostra).
  const areaDasRegioes = slab.regions.reduce(
    (soma, regiao) => soma + exactPolygonArea(regiao.outline),
    0,
  );
  const dasRegioes = slab.regions.reduce(
    (soma, regiao) => soma + exactPolygonArea(regiao.outline) * regiao.thickness,
    0,
  );
  const doResto = Math.max(0, exactPolygonArea(slab.outline) - areaDasRegioes) * slab.thickness;
  const dosBuracos = buracosDentro(slab, openings).reduce((soma, buraco) => {
    const retangulo = retanguloDe(buraco);
    return (
      soma + exactPolygonArea(retangulo) * thicknessAt(slab, centroide(retangulo)) * buraco.count
    );
  }, 0);
  return roundQuantity(Math.max(0, dasRegioes + doResto - dosBuracos), 3);
}

/** A borda: perímetro × espessura da peça — a fôrma de borda. m², duas casas. */
export function slabEdge(slab: Pick<Slab, 'outline' | 'thickness'>): number {
  return roundQuantity(polygonPerimeter(slab.outline) * slab.thickness, 2);
}

// ── Laje × viga (RN-371) ────────────────────────────────────────────────────────

/**
 * A altura da viga que conta, onde há laje: a seção menos a espessura da laje — o encontro é
 * da laje (D8). Sem laje (`slabThickness` ausente), a viga não muda (F13). Nunca abaixo de 0.
 *
 * **Simplificação desta etapa:** a espessura é a da laje do desenho, e não a da região que
 * passa por cima de cada viga — a viga não sabe onde está na planta, só em que parede.
 */
export function beamDepthBelowSlab(
  sectionDepth: number,
  slabThickness: number | undefined,
): number {
  if (slabThickness === undefined || slabThickness <= 0) return sectionDepth;
  return Math.max(0, sectionDepth - slabThickness);
}
